/**
 * Strips HTML tags from a paragraph config value for use in plain-text contexts
 * (e.g. MUI TextField label props). Block-level tags become newlines; HTML entities
 * are decoded. Matches the Gen2 strip behaviour used for prep instructions.
 */
export function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n')
    .replace(/<\/li>/gi, '\n').replace(/<\/div>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ').replace(/&#39;/g, "'").replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Extracts the plain text from the first <i> or <em> element in a Gen2 HTML
 * paragraph config value, falling back to the full stripped text if none found.
 * Used to pull the description line out of Gen2 combined label+description values.
 */
export function extractItalicText(html: string): string {
  const match = /<(?:i|em)[^>]*>([\s\S]*?)<\/(?:i|em)>/i.exec(html);
  return match ? stripHtml(match[1]) : stripHtml(html);
}

/**
 * Extracts the plain text from the first <b> or <strong> element in a Gen2 HTML
 * paragraph config value, falling back to the full stripped text if none is present.
 * Used with combined label+description paragraph config values.
 */
export function extractBoldText(html: string): string {
  const match = /<(?:b|strong)[^>]*>([\s\S]*?)<\/(?:b|strong)>/i.exec(html);
  return match ? stripHtml(match[1]) : stripHtml(html);
}

/**
 * Config token map — mirrors Gen2's AppConfig.ReplacableString enum.
 * These are the {Token} placeholders that paragraph config content and TOS
 * documents can embed, resolved from the active facility's configOverride.
 */
export interface ConfigTokens {
  PracticeName?: string;
  PortalName?: string;
  PortalFullName?: string;
  PortalShortName?: string;
  CopyrightName?: string;
  SiteName?: string;
  PracticeURL?: string;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Replaces {Token} placeholders in `text` with their resolved values.
 *
 * Case-insensitive — {PRACTICENAME}, {PracticeName}, and {practicename} all
 * resolve identically, matching Gen2's Regex.Replace(RegexOptions.IgnoreCase)
 * behaviour. Tokens whose value is falsy are left intact so they stay visible
 * in dev / misconfiguration rather than becoming silent empty strings.
 */
export function replaceConfigTokens(text: string, tokens: ConfigTokens): string {
  let result = text;
  for (const [key, value] of Object.entries(tokens)) {
    if (!value) continue;
    // Token keys come from our ConfigTokens map (PracticeName, etc.), not user input.
    // Use RegExp so case-insensitive match stays Unicode-length-safe (toLowerCase
    // can change string length, e.g. Turkish İ → i̇).
    // nosemgrep: eslint.detect-non-literal-regexp
    const pattern = new RegExp(`\\{${escapeRegExp(key)}\\}`, 'gi'); // nosemgrep: eslint.detect-non-literal-regexp -- trusted ConfigTokens key
    result = result.replace(pattern, () => value);
  }
  return result;
}

/**
 * Replaces token placeholders in a content string with runtime values.
 *
 * Handles two token formats that co-exist across paragraph config DefaultValues:
 *
 *   {0}, {1}, …        Positional — used by the majority of legacy DefaultValues
 *                       e.g. "The earliest date is {0}."
 *   {{name}}, {{url}}  Named — preferred format for new entries going forward
 *                       e.g. "Welcome, {{name}}!"
 *
 * Pass an array for positional replacement, a Record for named replacement, or both.
 *
 * @example positional
 *   interpolateContent("Available for {0} days. Call {1}.", ["30", "555-1234"])
 *   → "Available for 30 days. Call 555-1234."
 *
 * @example named
 *   interpolateContent("Welcome, {{name}}!", { name: "Jane" })
 *   → "Welcome, Jane!"
 */
export function interpolateContent(
  template: string,
  positional: (string | number)[],
  named?: Record<string, string | number>,
): string;
export function interpolateContent(
  template: string,
  named: Record<string, string | number>,
): string;
export function interpolateContent(
  template: string,
  posOrNamed: (string | number)[] | Record<string, string | number>,
  named?: Record<string, string | number>,
): string {
  if (!template) return '';

  let result = template;

  if (Array.isArray(posOrNamed)) {
    result = result.replace(/\{(\d+)\}/g, (_, i) =>
      String((posOrNamed as (string | number)[])[Number(i)] ?? ''),
    );
  } else {
    named = posOrNamed as Record<string, string | number>;
  }

  if (named) {
    result = result.replace(/\{\{(\w+)\}\}/g, (_, key) => String(named![key] ?? ''));
  }

  return result;
}
